import {
    FC,
    ReactElement,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Context } from "./context";
import { AppContext } from "../models/applicationState";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import { GridActionsCellItem, GridCellParams, GridColDef, GridRowId, GridRowModel } from "@mui/x-data-grid";
import { MeetingItemRepresentation, categoryColumn, contentColumn, dueDateColumn, externalEmailMarkerColumn, getMeetingItemFromRepresentation, hasRepresentationChanged, indexColumn, isoStringToDueDate, isStatusUpdated, statusColumn, statusTranslatedLabelOptions, typesColumn } from "../models/meetingItemRepresentation";
import EditableDueDateCell from "./editableDueDateCell";
import { Checkbox, Tooltip } from "@mui/material";
import { AttachmentsPane } from "./attachmentsPane";
import InfoIcon from '@mui/icons-material/Info';
import TaskIcon from '@mui/icons-material/Task';
import SignpostIcon from '@mui/icons-material/Signpost';
import { useTranslation } from "react-i18next";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditableGrid from "./editableGrid";
import ItemStatus from "./itemStatus";
import { TooltipIconWithBadge } from "./tooltipIconWithBadge";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    ItemEditingPanel, addStatusUpdateSystemCommentToItem, hasUserComments, addContentChangeCommentToItem,
    addDueDateChangeCommentToItem, addPrioritizationChangeCommentToItem, addRiskChangeCommentToItem, addActorChangeCommentToItem
} from "./itemEditingPanel";
import { bindActionCreators } from "../actions/actionCreators";
import { ItemActions } from "../actions/itemActions";
import * as itemActions from "../actions/itemActions";
import { ListActions } from "../actions/listActions";
import * as listActions from "../actions/listActions";
import { Meeting, MeetingStatus } from "../models/meeting";
import { MeetingItemStatus } from "../models/meetingItem";
import { useMsal } from "@azure/msal-react";
import CustomToolbar from "./customToolbar";
import ExternalEmailMarker from "./externalEmailMarker";

interface LibraryItemsPageTabProps {
    meeting: Meeting;
    rows: MeetingItemRepresentation[]
    readOnly?: boolean;
}

export const LibraryItemsPageTab: FC<LibraryItemsPageTabProps> = (props: LibraryItemsPageTabProps): ReactElement => {
    const ITEM = "item";
    const ITEM_SORT_FIELD = "item.sortField";
    const ITEM_SORT_DIRECTION = "item.sortDirection";
    const { t } = useTranslation();
    const appContext = useContext<AppContext>(Context);
    // Local optimistic overrides for in-flight updates (e.g. status flips before server confirms).
    // Reset whenever props.rows changes (server has reconciled).
    const [optimisticOverrides, setOptimisticOverrides] = useState<
        Map<GridRowId, MeetingItemRepresentation>
    >(new Map());
    useEffect(() => {
        setOptimisticOverrides(new Map());
    }, [props.rows]);
    const items = useMemo<MeetingItemRepresentation[]>(() => {
        if (optimisticOverrides.size === 0) return props.rows;
        return props.rows.map((row) => optimisticOverrides.get(row.id) ?? row);
    }, [props.rows, optimisticOverrides]);
    const [showAttachmentsForItem, setShowAttachmentsForItem] = useState(undefined);
    const [showEditingPanelForItem, setShowEditingPanelForItem] = useState(undefined);
    const { instance } = useMsal();

    const actions = useMemo(
        () => ({
            items: bindActionCreators(
                itemActions,
                appContext.dispatch
            ) as unknown as ItemActions,
            lists: bindActionCreators(
                listActions,
                appContext.dispatch
            ) as unknown as ListActions,
        }),
        [appContext.dispatch]
    );

    const openItemAttachments = (id: GridRowId, item: MeetingItemRepresentation) => () => {
        setShowAttachmentsForItem({ ...item, id });
    };

    const openItemEditingPanel = (item: MeetingItemRepresentation) => () => {
        setShowEditingPanelForItem({ item });
    };

    // Permission check: User can edit if they are the meeting creator OR the actor of that item
    const canEditItem = (item: MeetingItemRepresentation): boolean => {
        const userEmail = appContext.state.invoker?.email?.toLowerCase();
        if (!userEmail) return false;
        const isMeetingCreator = props.meeting?.creator?.toLowerCase() === userEmail;
        const isItemActor = item.actor?.toLowerCase() === userEmail;
        return isMeetingCreator || isItemActor;
    };

    // Permission check for due date: Can edit AND item is not prioritized
    const canEditDueDate = (item: MeetingItemRepresentation): boolean => {
        return canEditItem(item) && !item.prioritized;
    };

    const onItemUpdated = async (itemRepresentation: MeetingItemRepresentation) => {
        const userFullName = (appContext.state.invoker.lastName && appContext.state.invoker.firstName)
            ? (appContext.state.invoker?.firstName + ' ' + appContext.state.invoker?.lastName)
            : instance.getActiveAccount()?.name;

        // Find previous item for comparison
        const previousItem = items.find(row => row.id === itemRepresentation.id);


        // Meeting task: use regular comment functions

        if (previousItem && previousItem.status !== itemRepresentation.status) {
            addStatusUpdateSystemCommentToItem(itemRepresentation, userFullName, t);
        }
        if (previousItem && previousItem.dueDate !== itemRepresentation.dueDate) {
            addDueDateChangeCommentToItem(itemRepresentation, userFullName, itemRepresentation.dueDate, t);
        }
        if (previousItem && previousItem.risk !== itemRepresentation.risk) {
            addRiskChangeCommentToItem(itemRepresentation, userFullName, itemRepresentation.risk, t);
        }
        if (previousItem && previousItem.content !== itemRepresentation.content) {
            addContentChangeCommentToItem(itemRepresentation, userFullName, t);
        }
        if (previousItem && previousItem.prioritized !== itemRepresentation.prioritized) {
            addPrioritizationChangeCommentToItem(itemRepresentation, userFullName, itemRepresentation.prioritized, t);
        }
        if (previousItem && previousItem.actor !== itemRepresentation.actor) {
            addActorChangeCommentToItem(itemRepresentation, userFullName, itemRepresentation.actor || '', t);
        }
        const item = getMeetingItemFromRepresentation(itemRepresentation);
        await actions.items.save(itemRepresentation.meetingId, item);

        // Change meeting status to Draft if it was Planned
        if (props.meeting.status === MeetingStatus.Planned) {
            const updatedMeeting = { ...props.meeting, status: MeetingStatus.Draft };
            await actions.lists.save(updatedMeeting);
            await actions.lists.list(); // Refresh meeting list
        }
    }

    const handleStatusChange = async (
        itemId: GridRowId,
        newStatus: MeetingItemStatus,
    ) => {
        if (props.readOnly) return;

        const previousRow = items.find((row) => row.id === itemId);
        if (!previousRow || previousRow.status === newStatus) return;

        // Permission check: Only meeting creator or item actor can change status
        if (!canEditItem(previousRow)) return;

        const userFullName =
            appContext.state.invoker.lastName && appContext.state.invoker.firstName
                ? appContext.state.invoker?.firstName + " " + appContext.state.invoker?.lastName
                : instance.getActiveAccount()?.name;

        const updatedRow: MeetingItemRepresentation = {
            ...previousRow,
            status: newStatus,
        };

        addStatusUpdateSystemCommentToItem(updatedRow, userFullName, t);

        updatedRow.data = getMeetingItemFromRepresentation(updatedRow);
        if ((updatedRow as any).comments) {
            (updatedRow.data as any).comments = (updatedRow as any).comments;
        }

        setOptimisticOverrides((prev) => {
            const next = new Map(prev);
            next.set(itemId, updatedRow);
            return next;
        });
        await actions.items.save(updatedRow.meetingId, updatedRow.data);

        // Change meeting status to Draft if it was Planned
        if (props.meeting?.status === MeetingStatus.Planned) {
            const updatedMeeting = { ...props.meeting, status: MeetingStatus.Draft };
            await actions.lists.save(updatedMeeting);
            await actions.lists.list();
        }
    };

    const processRowUpdate = async (
        newRow: GridRowModel<MeetingItemRepresentation>,
    ) => {
        if (props.readOnly || !hasRepresentationChanged(newRow)) {
            return newRow;
        }

        const previousRow = items.find((row) => row.id === newRow.id);

        // Permission check for due date: Only creator/actor can edit, and only if not prioritized
        if (previousRow && previousRow.dueDate !== newRow.dueDate) {
            if (!canEditDueDate(newRow)) {
                // Reject the change by returning the previous row
                return previousRow;
            }
        }

        const userFullName =
            appContext.state.invoker.lastName && appContext.state.invoker.firstName
                ? appContext.state.invoker?.firstName + " " + appContext.state.invoker?.lastName
                : instance.getActiveAccount()?.name;

        // Due date change - add comment (permission already checked above)
        if (previousRow && previousRow.dueDate !== newRow.dueDate) {
            addDueDateChangeCommentToItem(newRow, userFullName, newRow.dueDate, t);
        }

        newRow.data = getMeetingItemFromRepresentation(newRow);
        if ((newRow as any).comments) {
            (newRow.data as any).comments = (newRow as any).comments;
        }

        setOptimisticOverrides((prev) => {
            const next = new Map(prev);
            next.set(newRow.id, newRow);
            return next;
        });
        await actions.items.save(newRow.meetingId, newRow.data);

        // Change meeting status to Draft if it was Planned
        if (props.meeting?.status === MeetingStatus.Planned) {
            const updatedMeeting = { ...props.meeting, status: MeetingStatus.Draft };
            await actions.lists.save(updatedMeeting);
            await actions.lists.list();
        }

        return newRow;
    };

    const itemAttachmentsActions = (id: GridRowId, row: MeetingItemRepresentation) => {
        return ([
            <GridActionsCellItem
                key="itemAttachments"
                icon={
                    <TooltipIconWithBadge
                        badgeContent={row.attachments}
                        titleKey='dashboard.itemAttachments'
                        icon={<AttachFileIcon />}
                    />
                }
                label={t('dashboard.itemAttachments_label')}
                onClick={openItemAttachments(id, row)}
                style={{ color: "var(--mui-palette-primary-main)" }}
            />,
            <GridActionsCellItem
                key="open"
                icon={<TooltipIconWithBadge titleKey={'general.open'} icon={<ChevronRightIcon />} badgeContent={hasUserComments(row, t) ? "i" : undefined} />}
                label="Edit"
                onClick={openItemEditingPanel(row)}
                style={{ color: "var(--mui-palette-primary-main)" }}
            />]
        );
    }

    // Helper to derive a domain string (including leading @) from an email
    const getDomainFromEmail = (email?: string): string | undefined => {
        if (email && email.includes('@')) {
            const domain = email.split('@')[1]?.toLowerCase();
            if (domain) {
                // Check if it's a valid domain (contains at least one dot and follows domain naming conventions)
                const isValidDomain = domain.includes('.') &&
                    /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(domain);

                if (isValidDomain) {
                    const isExternal = domain !== 'example.com'; // Example of checking for a specific domain
                    // Add label for external domains
                    return isExternal ? `@${domain} (external)` : `@${domain}`;
                } else {
                    // If it's not a valid domain (just a word/name), return without external label
                    return `@${domain}`;
                }
            }
        }
        return undefined;
    };

    const columns: GridColDef[] = [
        {
            ...indexColumn,
            valueFormatter: (_value, row) => row.index?.toString().padStart(2, '0')
        },
        { ...categoryColumn, editable: false },
        { ...contentColumn, editable: false, flex: 0.6 },
        {
            ...typesColumn as GridColDef,
            renderCell: (params) => (
                <Box>
                    {params.row.task
                        && (
                            <Tooltip title={t('item.taskType')}>
                                <span>
                                    <Checkbox
                                        disabled
                                        checkedIcon={<TaskIcon />}
                                        checked={true} />
                                </span>
                            </Tooltip>
                        )}
                    {params.row.decision && (
                        <Tooltip title={t('item.decisionType')}>
                            <span>
                                <Checkbox
                                    disabled
                                    checkedIcon={<SignpostIcon />}
                                    checked={true} />
                            </span>
                        </Tooltip>
                    )}
                    {params.row.info
                        && (
                            <Tooltip title={t('item.informationType')}>
                                <span>
                                    <Checkbox
                                        disabled
                                        // icon={<InfoOutlinedIcon />}
                                        checkedIcon={<InfoIcon />}
                                        checked={true} />
                                </span>
                            </Tooltip>
                        )}
                </Box>
            ),
        },
        { field: 'actor', headerName: 'item.actor', flex: 0.3, editable: false },
        {
            // Show yellow "E" marker after actor email for external addresses
            ...externalEmailMarkerColumn,
            renderCell: (params) => {
                const actor: string | undefined = params.row.actor;
                return (<ExternalEmailMarker email={actor} internalFromEmail={props.meeting?.creator} />);
            }
        },
        {
            ...dueDateColumn,
            editable: false,
            minWidth: 145,
            maxWidth: 145,
            renderCell: (params) => {
                const row = params.row as MeetingItemRepresentation;
                const cellReadOnly = props.readOnly || !canEditDueDate(row);
                return (
                    <EditableDueDateCell
                        taskId={String(row.id)}
                        dueDate={row.dueDate}
                        readOnly={cellReadOnly}
                        onChange={(_taskId, newISODate) => {
                            const newRow: MeetingItemRepresentation = {
                                ...row,
                                dueDate: isoStringToDueDate(newISODate) as Date,
                            };
                            if (!newRow.task) newRow.task = true;
                            processRowUpdate(newRow);
                        }}
                    />
                );
            },
        },
        {
            ...statusColumn, editable: false,
            type: 'singleSelect',
            valueFormatter: (value) => t(`item_status.${String(value)}` as any),
            valueOptions: statusTranslatedLabelOptions(t),
            renderCell: (params: GridCellParams) => (
                params.row.task ? (
                    <ItemStatus
                        status={params.row.status}
                        lastModifiedBy={params.row.statusLastUpdatedBy}
                        onChange={!props.readOnly && canEditItem(params.row) ? (newStatus) => handleStatusChange(params.row.id, newStatus) : undefined}
                    />
                ) : <Box>{t('item.notAvailable')}</Box>
            ),
        },
    ];

    const bottomColumns: GridColDef[] = columns;

    return (
        <Box sx={{ pt: 2 }}>
            <CssBaseline />

            {showAttachmentsForItem
                && (
                    <AttachmentsPane
                        allowModifications={!props.readOnly}
                        height='70vh'
                        uploadPrefix={`items/meetings/${showAttachmentsForItem.meetingId}/items/${showAttachmentsForItem.id}/`}
                        meetingId={showAttachmentsForItem.meetingId}
                        itemId={showAttachmentsForItem.id}
                        onClose={() => setShowAttachmentsForItem(undefined)}
                        onAttachmentsCountChanged={(count, _meetingId, itemId) => {
                            const existing = items.find((i) => i.id === itemId);
                            if (!existing) return;
                            const updated = { ...existing, attachments: count };
                            setOptimisticOverrides((prev) => {
                                const next = new Map(prev);
                                next.set(itemId, updated);
                                return next;
                            });
                        }}
                    />
                )}

            {showEditingPanelForItem
                && (
                    <ItemEditingPanel
                        allowAttachmentsEditing={!props.readOnly}
                        readOnly={showEditingPanelForItem.item.isSnapshot}
                        allowMetadataEditing={!props.readOnly && canEditItem(showEditingPanelForItem.item)}
                        meetingItem={showEditingPanelForItem.item} open={showEditingPanelForItem}
                        meeting={props.meeting}
                        onClose={() => setShowEditingPanelForItem(undefined)}
                        onUpdate={onItemUpdated}
                        onAttachmentsCountChanged={(count, _meetingId, itemId) => {
                            const existing = items.find((i) => i.id === itemId);
                            if (!existing) return;
                            const updated = { ...existing, attachments: count };
                            setOptimisticOverrides((prev) => {
                                const next = new Map(prev);
                                next.set(itemId, updated);
                                return next;
                            });
                        }}
                    />
                )}

            <Container maxWidth={false} disableGutters>
                <EditableGrid
                    rows={items}
                    columns={bottomColumns}
                    height='80vh'
                    modelType={ITEM}
                    actionsHeaderKey=''
                    extraActions={itemAttachmentsActions}
                    toolbar={CustomToolbar}
                    processRowUpdate={!props.readOnly ? processRowUpdate : undefined}
                />
            </Container>
        </Box>
    );
};
